import { type Combatant, type EncounterState, type WeaponProfile, superheavyBarsSkirmish, activeAttack, weaponInstanceKey, isWeaponLoaded, canUseLimitedWeapon, canUseAction, findAffordance, grantedActionAllowanceBonus, isItemDestroyed, threatValue } from "@lancer-vtt/domain";
import { attackMounts, checkBarrageContinuation, checkOrdnance, checkOrdnanceWindow, isRiderShot, liveBackswingReservations, lockPairingBlock, mountKeyOf, overwatchReachOf, reactionShotGrantFor, weaponFireBlock, type OverwatchReach } from "@lancer-vtt/rules";

/**
 * The Skirmish chooser's render-state verdict — see {@link classifySkirmishChooser}.
 */
export type SkirmishChooserState =
  | { kind: "armable-primary" }
  | { kind: "mid-attack-rider"; riderWeapon: WeaponProfile | undefined }
  | { kind: "spent" };

/**
 * {@link SkirmishChooser}'s render-state derivation — the Skirmish twin of {@link classifyBarrageSlot},
 * but coarser: a Skirmish caps at exactly one primary + one eligible Aux rider (Core p.70), so there's
 * no per-slot legality table to mirror, just three states off the synced mid-attack lock
 * ({@link activeAttack}, which covers every attack kind, not just Barrage):
 *
 * - `"armable-primary"` — no lock open. Unconditional weapon cards.
 * - `"mid-attack-rider"` — a lock is open for this actor's Skirmish. `riderWeapon` is the one
 * weapon still able to pair on the lock's mount ({@link lockPairingBlock}, the same one-primary-
 * plus-one-Aux rule `isAttackLockContinuation` gates the shot on), or `undefined` if none remains
 * (defensive — a Skirmish lock should only stay open while a partner is genuinely live).
 * - `"spent"` — the actor's Quick action is gone and no lock is open (mirrors {@link BarrageChooser}'s
 * `complete`; it does not consult the once-per-turn "skirmish" ledger, matching `complete`'s own
 * raw-budget granularity rather than re-deriving that wall here).
 *
 * Overwatch shares this chooser (`aim.reaction === true`) but is a reaction — it never opens or reads
 * a mid-attack lock, so `isOverwatch` short-circuits straight to `"armable-primary"`.
 *
 * **Exported for testability, not reuse** — the repo has no jsdom/headless React renderer (see
 * `gmDirectiveEmit.integration.test.ts`'s doc), so a unit test drives this pure classifier directly
 * with a constructed `EncounterState`/lock rather than a DOM render, the same shape
 * {@link classifyBarrageSlot} is tested.
 */
export function classifySkirmishChooser(args: { combatant: Combatant; state: EncounterState; isOverwatch: boolean }): SkirmishChooserState {
  const { combatant: c, state, isOverwatch } = args;
  if (isOverwatch) return { kind: "armable-primary" };
  const weapons = c.base.weapons ?? [];
  const turn = state.activation.turn;
  const isActiveActor = turn.phase === "acting" && turn.actor === c.id;
  const lock = isActiveActor ? activeAttack(state.activation) : null;
  if (lock !== null) {
    const riderWeapon = weapons.find((w) => lockPairingBlock({ state, attackerId: c.id, weapon: weaponInstanceKey(w), lock }) === undefined);
    return { kind: "mid-attack-rider", riderWeapon };
  }
  // A live Backswing Cut reservation re-lights the Skirmish row past its own per-turn ledger — the
  // SAME override `UnitActionRow`'s gate applies. The chooser must honor it too, or it renders
  // "SKIRMISH COMPLETE" for a row the player was just invited to click: `skirmishSpent` below reads
  // TRUE the instant the ledger is used, which is exactly when a reservation goes live.
  if (isActiveActor && liveBackswingReservations(c, turn).length > 0) return { kind: "armable-primary" };
  if (isActiveActor && turn.phase === "acting" && weapons.length > 0) {
    const quickAvailable = turn.budget.actions >= 1;
    // The Skirmish is spent when its once-per-turn allowance is used up — NOT merely when the whole
    // action budget is empty. A mech that Skirmished still holds its 2nd Quick for a DIFFERENT action,
    // so `budget.actions >= 1` alone leaves the chooser re-offering every weapon as armable even though
    // a second Skirmish is illegal (Core p.65, "can't take the same action twice") — the reducer then
    // toast-rejects the click. Reading the per-action ledger closes the chooser to COMPLETE instead.
    // Allowance composition mirrors `checkActionUseLegality` (`rules/economy.ts`).
    const skirmishAllowance =
      (c.base.actionAllowances?.skirmish ?? findAffordance(c.base.affordances, "skirmish")?.cap?.max ?? 1) +
      grantedActionAllowanceBonus(c.current.resources, "skirmish");
    const skirmishSpent = !canUseAction(turn.budget, "skirmish", skirmishAllowance);
    if (!quickAvailable || skirmishSpent) return { kind: "spent" };
  }
  return { kind: "armable-primary" };
}

/**
 * The two ways an **ORDNANCE**-tagged weapon card is greyed by a wall that is about *timing*, not
 * about the weapon's own state (`action-ordering-audit` 3.2, RAW Core p.105 — "This weapon can only
 * be fired before the user moves or takes any other actions on their turn, excepting PROTOCOLS …
 * and cannot be used for OVERWATCH"). `undefined` ⇒ nothing about Ordnance blocks this card.
 *
 * **Both legs IMPORT the engine wall rather than re-deriving it**, the same stance
 * {@link classifyBarrageSlot} takes on {@link checkBarrageContinuation} — and here it is the whole
 * point of the change. The window is one cost-keyed predicate shared by the browser and the server
 * ({@link import("@lancer-vtt/domain").turnBudgetVirgin}, reached through `checkOrdnance` →
 * `checkOrdnanceWindow` → `isTurnVirgin`), so the greying cannot drift into a second, parallel
 * notion of "is the window open". In particular the window is **COST-KEYED**: a free activation or
 * an Overcharge grants a point without spending one and leaves the card ARMABLE — a card that greys
 * after an Overcharge is wrong, not conservative.
 *
 * - `"ordnance-window"` — the live turn-virgin window has closed (`checkOrdnance`, which also owns
 *   the `midAttack` split: a continuation reads the {@link AttackLock.ordnanceWindow} SNAPSHOT
 *   frozen at `open_attack`, never the live window, so an Ordnance-opened Barrage's second shot
 *   stays armable after the opener spent its own points).
 * - `"ordnance-overwatch"` — a **flat** prohibition, independent of any window: `checkOverwatchRange`
 *   (`economy/attackGates.ts`) rejects an Ordnance weapon for Overwatch before it even measures
 *   threat, and `overwatchWeaponThreats` (`overwatchReach.ts`) already filters Ordnance out of the
 *   offer. `checkOrdnance` cannot see this leg — `isTurnVirgin` returns `true` off the watcher's own
 *   turn, so the window reads OPEN for every reaction — which is why it is a separate check and not
 *   a `midAttack`-style argument to the same call.
 */
function ordnanceCardBlock(args: {
  weapon: WeaponProfile;
  combatant: Combatant;
  state: EncounterState;
  /** The Skirmish chooser armed as an Overwatch reaction (`aim.reaction === true`). Never true for Barrage. */
  isOverwatch: boolean;
  /** True for a mid-attack-lock CONTINUATION card — threaded straight through to `checkOrdnance`. */
  midAttack: boolean;
  /** The card is a granted reaction shot ({@link isGrantedShotCard}), which Ordnance may fire. */
  grantedShot?: boolean;
}): "ordnance-window" | "ordnance-overwatch" | undefined {
  const { weapon: w, combatant: c, state, isOverwatch, midAttack, grantedShot } = args;
  if (!w.ordnance || grantedShot === true) return undefined;
  if (isOverwatch) return "ordnance-overwatch";
  const verdict = checkOrdnance({ state, attacker: c.id, weapon: weaponInstanceKey(w), midAttack });
  return verdict.ok ? undefined : "ordnance-window";
}

/**
 * Per-weapon usability of a Skirmish **primary** card (the armable-primary state) — the Skirmish twin
 * of {@link classifyBarrageSlot}'s pre-`midAttack` reads, greying a card exactly when the reducer would
 * reject its fire: `checkWeaponRecharge` (discharged), `checkLoaded` (unloaded), `checkLimited`
 * (depleted `Limited {N}`), a Superheavy weapon on the Overwatch arm (`checkSuperheavySkirmish`),
 * and {@link ordnanceCardBlock}'s two Ordnance timing walls. `"armable"`
 * otherwise. Without this classification a
 * depleted/unloaded/discharged weapon looks armable and the engine toast-rejects the click.
 * Recharge rides `current.affordanceState`; Loading rides `resources`; Limited rides
 * `resources` via the shared {@link canUseLimitedWeapon} predicate.
 *
 * **Ordnance is checked LAST, after the weapon-state legs.** Order matters on the Howitzer, which is
 * `tg_loading` **and** `tg_ordnance`: once it has fired it is UNLOADED, and "reload with STABILIZE"
 * is the actionable, cross-turn message, where the window's own state is moot until it is loaded
 * again. Keeping Ordnance last also leaves every pre-existing verdict byte-identical.
 */
export function classifySkirmishCard(args: {
  weapon: WeaponProfile;
  combatant: Combatant;
  state: EncounterState;
  /** Armed as an Overwatch reaction — `SkirmishChooser`'s own `aim.reaction === true`. */
  isOverwatch: boolean;
  /**
   * The card is a **Barrage** pick (the command menu's shared mount and fire rows), where a
   * Superheavy is legal — it is the one attack a Superheavy can make. Absent on every Skirmish,
   * Overwatch and Prepare card.
   */
  barrage?: boolean;
  /**
   * The card is a granted reaction shot ({@link isGrantedShotCard}: Covering Fire, Impending Threat,
   * Moving Target): Ordnance's Overwatch ban does not apply to it (`reactionShotGrant`).
   */
  grantedShot?: boolean;
}): "armable" | "discharged" | "unloaded" | "limited" | "destroyed" | "superheavy-skirmish" | "superheavy-overwatch" | "ordnance-window" | "ordnance-overwatch" {
  const { weapon: w, combatant: c, state, isOverwatch, barrage, grantedShot } = args;
  const key = weaponInstanceKey(w);
  // A System Trauma result (or `edit_combatant_base`) can destroy
  // any weapon (mech mount OR NPC synthetic singleton mount, same `isItemDestroyed` read the
  // reducer's own participation gates use), permanently excluding it from Skirmish. Checked first:
  // a destroyed weapon can never be discharged/unloaded/limited-relevant again.
  if (isItemDestroyed(c.current.resources, key)) return "destroyed";
  if (w.recharge && c.current.affordanceState?.[key]?.discharged) return "discharged";
  if (w.loading && !isWeaponLoaded(c.current.resources, key)) return "unloaded";
  if (w.limited !== undefined && !canUseLimitedWeapon(c.current.affordanceState, key, w.limited)) return "limited";
  // Superheavy fires only as part of a Barrage (Core p.70), so never in a Skirmish or on Overwatch:
  // the server's `checkSuperheavySkirmish` refuses both (verb "skirmish" / "overwatch"), and
  // `overwatchWeaponThreats` leaves it out of the Overwatch offer. The Leviathan's Standard profile
  // is the exception (lancer-data `skirmish: true`, Core p.134).
  if (!barrage && superheavyBarsSkirmish(w)) return isOverwatch ? "superheavy-overwatch" : "superheavy-skirmish";
  // Only the armable-primary branch renders these cards, so a Skirmish card is never a mid-attack
  // CONTINUATION — the Aux rider gets its own button. `midAttack: false` is a fact here, not a default.
  return ordnanceCardBlock({ weapon: w, combatant: c, state, isOverwatch, midAttack: false, grantedShot }) ?? "armable";
}

/**
 * Is this reaction card a shot a rule grants (Covering Fire's Heavy ranged weapon at the watcher's
 * marked target, Impending Threat, Moving Target)? The room reads the same grant
 * (`reactionShotGrant`): gated on the weapon's Range rather than its Threat, and Ordnance allowed.
 */
export function isGrantedShotCard(args: { weapon: WeaponProfile; watcherId: string; state: EncounterState; targetId: string }): boolean {
  const { weapon, watcherId, state, targetId } = args;
  return reactionShotGrantFor({ state, attacker: watcherId, target: targetId, weapon }) !== undefined;
}

/**
 * **An Overwatch shot at a fixed target: why this weapon can't reach it**, or `undefined` when it
 * can (`projects/overwatch-mid-move.md` 3.2). `reach` is {@link overwatchReachOf} the watcher
 * against the target, the same read the Overwatch offer's eligibility and prompt use, so a card
 * greyed here is exactly a weapon the prompt did not name. It mirrors the server wall a
 * `request_attack {reaction: true}` meets (`checkOverwatchRange`'s "N away — outside … threat
 * range"). Kept apart from {@link classifySkirmishCard}'s verdict union: it needs the target, which
 * only the take flow has.
 */
export function overwatchThreatShortfall(args: { weapon: WeaponProfile; reach: OverwatchReach | undefined }): { threat: number; distance: number } | undefined {
  const { weapon: w, reach } = args;
  if (reach === undefined) return undefined;
  if (reach.weapons.some((r) => r.weaponRef === weaponInstanceKey(w))) return undefined;
  return { threat: threatValue(w.range ?? []), distance: reach.distance };
}

/**
 * The weapon the Overwatch take flow arms up front: the watcher's only card that is armable
 * ({@link classifySkirmishCard}) and reaches the target ({@link overwatchThreatShortfall}), else
 * `""` (none, or several: the watcher picks, with the out-of-threat cards greyed and listed last).
 * A granted shot arms the watcher's only armable weapon the grant allows ({@link isGrantedShotCard}).
 */
export function overwatchTakeWeapon(args: { combatant: Combatant; state: EncounterState; targetId: string }): string {
  const { combatant: c, state, targetId } = args;
  const reach = overwatchReachOf(state, { watcherId: c.id, targetId });
  const weapons = c.base.weapons ?? [];
  // A granted shot (the target carries this watcher's grant) arms its only working weapon the grant allows.
  const granted = weapons.filter(
    (w) => isGrantedShotCard({ weapon: w, watcherId: c.id, state, targetId }) && classifySkirmishCard({ weapon: w, combatant: c, state, isOverwatch: true, grantedShot: true }) === "armable",
  );
  if (granted.length === 1) return weaponInstanceKey(granted[0]!);
  const usable = weapons.filter(
    (w) => classifySkirmishCard({ weapon: w, combatant: c, state, isOverwatch: true }) === "armable" && overwatchThreatShortfall({ weapon: w, reach }) === undefined,
  );
  return usable.length === 1 ? weaponInstanceKey(usable[0]!) : "";
}

/**
 * Per-weapon Barrage card legality — **imports the engine wall**
 * ({@link checkBarrageContinuation}) instead of re-deriving it, so this can never drift the way the
 * old hand-rolled `w.mount === WEAPON_SIZE_SUPERHEAVY || firedSuperheavy || fired.length >= 2` heuristic
 * did (it went stale the moment the reducer moved off the flat two-cap to a distinct-mounts +
 * Aux-rider model, silently greying a legal rider-as-3rd-shot).
 * `"discharged"`/`"unloaded"`/`"limited"` (the shared usability predicate's chooser-greying half) are
 * checked BEFORE the verdict — they give specific per-slot messaging the generic `ok`/`err` doesn't.
 * `"ordnance-window"` ({@link ordnanceCardBlock}) joins them, and is reachable on an OPENER in the
 * ordinary table case: a Full action survives a move (`fullAvailable` only reads `budget.actions`),
 * so "move, then Barrage the Howitzer" leaves the card armable-looking while the server refuses it.
 * A CONTINUATION passes `midAttack` through, so it reads the lock's window SNAPSHOT and an
 * Ordnance-opened Barrage's second shot is NOT greyed.
 * Exported so it can be driven directly in a unit test (the repo has no jsdom/headless React renderer —
 * see `gmDirectiveEmit.integration.test.ts` — so this pure classifier, not a DOM render, is what a test
 * exercises to prove a rider-as-3rd-shot card comes back `"armable"`).
 */
export function classifyBarrageSlot(args: {
  weapon: WeaponProfile;
  combatant: Combatant;
  state: EncounterState;
  /** The weapons fired so far this Barrage, off the synced lock (`[]` when no lock is open). */
  fired: readonly string[];
  /**
   * The committed mount keys: the lock's once a shot has landed, else the opener's pick (the
   * chooser's mount toggles). A weapon off them is `"uncommitted"` on the opener.
   */
  mounts: readonly string[];
  midAttack: boolean;
  fullAvailable: boolean;
}): "armable" | "rider" | "fired" | "blocked" | "uncommitted" | "discharged" | "unloaded" | "limited" | "destroyed" | "ordnance-window" {
  const { weapon: w, combatant: c, state, fired, mounts, midAttack, fullAvailable } = args;
  const key = weaponInstanceKey(w);
  // Same destroyed check as `classifySkirmishCard`, checked before
  // even `fired` (a destroyed weapon was never a legal shot to begin with).
  if (isItemDestroyed(c.current.resources, key)) return "destroyed";
  if (fired.includes(key)) return "fired";
  if (w.recharge && c.current.affordanceState?.[key]?.discharged) return "discharged";
  if (w.loading && !isWeaponLoaded(c.current.resources, key)) return "unloaded";
  if (w.limited !== undefined && !canUseLimitedWeapon(c.current.affordanceState, key, w.limited)) return "limited";
  // The Ordnance timing wall, opener AND continuation (`checkOrdnance` owns the live-window vs.
  // lock-snapshot split). Inert off the actor's own turn — `isTurnVirgin` reads the window OPEN
  // there — so it never collides with the `!fullAvailable ⇒ "blocked"` opener case below.
  //
  // **`state` may be `previewActivation`'s hypothetical post-activation state, and the two lanes
  // agree there by construction** (`pre-activation-affordances` 2.3 / D4 met this file's Ordnance
  // greying at integration). `BarrageChooser` composes against `inspectorEconomy.state`, which is
  // the previewed turn for a `"ready"` unit its viewer controls — so such a mech IS the acting
  // actor on the state that arrives here, and `fullAvailable` is true rather than the `"blocked"`
  // that an un-previewed idle read would give. The Ordnance leg is inert in BOTH readings and for
  // the same rule: raw-idle takes `isTurnVirgin`'s not-my-turn short-circuit, previewed takes a
  // budget whose `spent` the fold mints at `{actions: 0, move: 0}`. A `"ready"` unit has by
  // definition spent nothing this turn, so the window is genuinely open and an Ordnance card is
  // genuinely armable as its first action — which is exactly what the server concludes, because
  // `refereeIntent`'s own `checkOrdnance` runs against the same object via the room's
  // `resolutionState`. Neither lane can grey the other's card by accident.
  // (`isOverwatch: false` is a fact, not a default — a Barrage is a Full action, never a reaction —
  // so the `"ordnance-overwatch"` leg is unreachable here and stays out of this function's union.)
  if (ordnanceCardBlock({ weapon: w, combatant: c, state, isOverwatch: false, midAttack }) === "ordnance-window") {
    return "ordnance-window";
  }
  if (midAttack) {
    const verdict = checkBarrageContinuation({ state, attackerId: c.id, weapon: key, fired, mounts });
    if (!verdict.ok) return "blocked";
    // A legal continuation that is its mount's rider (no bonus damage) — the SAME predicate the
    // room stamps the `noBonus` marker with.
    return isRiderShot({ state, attackerId: c.id, fired, weaponKey: key }) ? "rider" : "armable";
  }
  // Opener: a weapon on a committed mount, provided the Full action is available.
  if (!fullAvailable) return "blocked";
  return mounts.includes(mountKeyOf(c.base.mounts, key)) ? "armable" : "uncommitted";
}
